<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSelectMulti from "@/components/multi/ArgonSelectMulti.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import { zoneStore } from "@/_store/zoneStore";
import { errorUtils } from "@/utils";
const props = defineProps({
  zoneId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["close", "success"]);
const vuexStore = useStore();
const zonesStore = zoneStore();
const bootstrap = vuexStore.state.bootstrap;
const modalRef = ref(null);
const snackbar = ref(null);
const disableButton = ref(false);

const createDefaultForm = () => ({
  account_id: "",
  name: "",
  postal_codes: [],
  active: true,
});

const form = ref(createDefaultForm());
const rules = {
  name: {
    required: helpers.withMessage("Nombre es requerido", required),
  },
  postal_codes: {
    required: helpers.withMessage("Códigos postales es requerido", required)
     
  },
};

const v$ = useVuelidate(rules, form);
const isEdit = computed(() => !!props.zoneId);
const modalTitle = computed(() => (isEdit.value ? "Modificar" : "Nueva"));

function fillForm(zone) {
  form.value = {
    account_id: zone?.account_id || "",
    name: zone?.name || "",
    postal_codes: Array.isArray(zone?.postal_codes)
      ? [...zone.postal_codes]
      : [],
    active: zone?.active ?? true,
  };
}

watch(
  () => props.zoneId,
  (value) => {
    disableButton.value = false;
    fillForm(null);
    v$.value.$reset();
    if (value) {
      zonesStore.getZoneById(value);
    }
  },
  { immediate: true }
);

watch(
  () => zonesStore.zone,
  (value) => {
    if (!props.zoneId || !value) return;
    if (String(value._id || "") !== String(props.zoneId)) return;
    fillForm(value);
    v$.value.$reset();
  }
);

watch(
  () => zonesStore.zoneCreated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => zonesStore.zoneUpdated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => zonesStore.error,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById("modalZone"), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();
});

function closeModal() {
  emit("close");
}

const closeSnackbar = () => {
  snackbar.value = null;
};

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function saveZone() {
  const errors = await validate();

  if (errors.length > 0) {
    let message = "";
    for (const error of errors) {
      if (!message.includes(`<li>${error.$message}</li>`)) {
        message = `${message}<li>${error.$message}</li>`;
      }
    }

    snackbar.value = {
      title: "Error",
      message: `<ul>${message}</ul>`,
      type: "danger",
      color: "white",
    };
    return;
  }
  disableButton.value = true;
  const payload = {
    name: form.value.name.trim(),
    postal_codes: form.value.postal_codes
      .map((cp) => String(cp).trim())
      .filter(Boolean),
  };
  if (isEdit.value) {
    zonesStore.updateZone(
      {
        name: payload.name,
        postal_codes: payload.postal_codes,
      },
      props.zoneId
    );
    return;
  }
  zonesStore.createZone({
    name: payload.name,
    postal_codes: payload.postal_codes,
  });
}
</script>

<template>
  <modal-base id="modalZone" ref="modalRef" width="35rem" @hide="closeModal">
    <template #header>
      <h6 class="modal-title mb-0">
        {{ modalTitle }}
      </h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
       <alert-message
        title="Aviso"
        type="info"
        class="mb-3"
      >
        Las modificaciones de las zonas no tendrán efecto en las planificaciones en curso.
      </alert-message>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label for="zone-name" class="col-form-label required">Nombre</label>
        </div>
        <div class="col">
          <argon-input
            id="zone-name"
            v-model="form.name"
            :error="v$.name.$error"
            :is-required="true"
            :max-length="25"
          />
        </div>
      </div>

      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label v-tooltip="'Sirven para indicar qué códigos postales pertenecen a esta zona.'" for="zone-postal-codes"  class="col-detail col-form-label required"
            >Códigos postales
             <sup                
                  class="text-primary sup-question"
                  >?</sup>
            </label
          >
        </div>
        <div class="col">
          <argon-select-multi
            id="zone-postal-codes"
            v-model="form.postal_codes"
            :error="v$.postal_codes.$error"
          />
        </div>
      </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :loading="disableButton"
        :disable-button="disableButton"
        @click="saveZone"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
